/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import './GlobalClients.css';
import Spinner from './Spinner';
import axios from 'axios';
import DataTable, { ActionMenu } from './DataTable';
import { Icons } from './ActionIcons';

type GlobalClient = {
  _id?: string;
  clientGlobalId: string;
  clientName: string;
  createdBy?: { fullName?: string; username?: string } | string;
  createdAt?: string;
  updatedAt?: string;
};

const GlobalClients: React.FC = () => {
  
  const [rows, setRows] = useState<GlobalClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<GlobalClient | null>(null);
  const [creating, setCreating] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState<{ visible: boolean; message: string; variant?: 'info' | 'danger' | 'success' }>({
    visible: false,
    message: '',
    variant: 'info',
  });

  const [confirmState, setConfirmState] = useState<{
    visible: boolean;
    title?: string;
    message?: string;
    onConfirm?: () => void;
  }>({ visible: false });

  // theme handled globally in AppLayout

  const filteredRows = useMemo(() => {
    let list = rows.slice();

    if (Object.keys(columnFilters).length > 0) {
      list = list.filter((item) => {
        return Object.entries(columnFilters).every(([key, value]) => {
          if (!value) return true;
          const lower = value.toLowerCase();
          const createdByText = item.createdBy
            ? typeof item.createdBy === 'string'
              ? item.createdBy
              : item.createdBy.fullName || item.createdBy.username || ''
            : '';

          const cellValue = String(
            key === 'createdBy'
              ? createdByText
              : key === 'createdAt' || key === 'updatedAt'
                ? item[key as keyof GlobalClient] || ''
                : (item as any)[key] || ''
          ).toLowerCase();

          return cellValue.includes(lower);
        });
      });
    }

    list.sort((a, b) => {
      const getVal = (row: GlobalClient, c: string) => {
        if (c === 'createdBy') {
          return row.createdBy
            ? typeof row.createdBy === 'string'
              ? row.createdBy
              : row.createdBy.fullName || row.createdBy.username || ''
            : '';
        }
        return (row as any)[c] || '';
      };

      const va = getVal(a, sortColumn);
      const vb = getVal(b, sortColumn);

      if ((sortColumn === 'createdAt' || sortColumn === 'updatedAt') && va && vb) {
        return sortDir === 'asc'
          ? new Date(va).getTime() - new Date(vb).getTime()
          : new Date(vb).getTime() - new Date(va).getTime();
      }

      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb), 'pt-BR')
        : String(vb).localeCompare(String(va), 'pt-BR');
    });

    return list;
  }, [rows, columnFilters, sortColumn, sortDir]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [filteredRows.length, pageSize, currentPage]);

  // eslint-disable-next-line @typescript-eslint/no-use-before-define, react-hooks/exhaustive-deps
  useEffect(() => {
    loadRows();
  }, []);

  const getGlobalClientsUrls = (id?: string) => {
    const hostname = window.location.hostname;
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
    const base = isLocalHost ? '' : process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const suffix = id ? `/${id}` : '';

    const primary = baseNoSlash
      ? `${baseNoSlash}/api/global-clients${suffix}`
      : `/api/global-clients${suffix}`;
    const fallback = baseNoSlash
      ? `${baseNoSlash}/api/globalClients${suffix}`
      : `/api/globalClients${suffix}`;

    return [primary, fallback];
  };

  const requestGlobalClientsApi = async <T = any>(
    method: 'get' | 'post' | 'put' | 'delete',
    id?: string,
    data?: any
  ): Promise<T> => {
    const urls = getGlobalClientsUrls(id);
    let lastError: any;

    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      try {
        const res = await axios.request<T>({
          method,
          url,
          data,
          withCredentials: true,
        });
        return res.data;
      } catch (err: any) {
        const status = err?.response?.status;
        const isLast = i === urls.length - 1;
        if (status === 404 && !isLast) {
          continue;
        }
        lastError = err;
        break;
      }
    }

    throw lastError;
  };

  const getErrorMessage = (err: any, fallbackMessage: string) => {
    if (err?.response?.status === 404) {
      return 'Endpoint de clientes globais não encontrado (404). Atualize/reinicie o backend para carregar a nova rota.';
    }
    return err?.response?.data?.message || err?.message || fallbackMessage;
  };

  const loadRows = () => {
    setLoading(true);
    requestGlobalClientsApi<any[]>('get')
      .then((data) => {
        setRows(Array.isArray(data) ? data : []);
        setError(null);
      })
      .catch((err) => {
        setError(getErrorMessage(err, 'Erro ao carregar clientes globais'));
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  const openCreate = () => {
    setEditing({ clientName: '' } as GlobalClient);
    setCreating(true);
    setShowModal(true);
  };

  const openEdit = (item: GlobalClient) => {
    setEditing({ ...item });
    setCreating(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const saveRow = async () => {
    if (!editing || saving) return;

    if (!editing.clientName?.trim()) {
      showToast('Nome do cliente é obrigatório', 'danger');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        clientName: editing.clientName.trim(),
      };

      if (creating) {
        await requestGlobalClientsApi('post', undefined, payload);
        showToast('Cliente global criado com sucesso', 'success');
        await requestGlobalClientsApi('put', editing._id, payload);
        showToast('Cliente global atualizado com sucesso', 'success');
      }

      closeModal();
      loadRows();
    } catch (err: any) {
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = (item: GlobalClient) => {
    showConfirm(
      'Excluir Cliente Global',
      `Tem certeza que deseja excluir o cliente global "${item.clientGlobalId} - ${item.clientName}"?`,
      async () => {
        try {
          await requestGlobalClientsApi('delete', item._id);
          showToast('Cliente global excluído com sucesso', 'success');
          loadRows();
        } catch (err: any) {
          showToast(getErrorMessage(err, 'Erro ao excluir cliente global'), 'danger');
        }
      }
    );
  };

  const showToast = (message: string, variant: 'info' | 'danger' | 'success' = 'info') => {
    setToast({ visible: true, message, variant });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 4000);
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setConfirmState({ visible: true, title, message, onConfirm });
  };

  const hideConfirm = () => setConfirmState({ visible: false });

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  };

  const getCreatedByName = (createdBy?: { fullName?: string; username?: string } | string) => {
    if (!createdBy) return '-';
    if (typeof createdBy === 'string') return createdBy;
    return createdBy.fullName || createdBy.username || '-';
  };

  if (loading) {
    return (
      <>
        <Spinner />
      </>
    );
  }

  return (
    <>
      {/* Header is provided by AppLayout - do not render it here */}
      <div className="container py-5" style={{ marginTop: 80 }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h1 className="mb-0">Clientes Globais</h1>
            <p className="lead mb-0">Crie e mantenha o cadastro global de clientes da solução</p>
          </div>
          <div>
            <button className="btn btn-secondary global-clients-uniform-btn" onClick={openCreate}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 0a1 1 0 011 1v6h6a1 1 0 110 2H9v6a1 1 0 11-2 0V9H1a1 1 0 110-2h6V1a1 1 0 011-1z" />
              </svg>
              <span className="ms-2">Novo Cliente Global</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <DataTable
          columns={[
            { key: 'clientGlobalId', label: 'Client Global ID', sortable: true },
            { key: 'clientName', label: 'Nome do Cliente', sortable: true },
            {
              key: 'createdBy',
              label: 'Criado por',
              sortable: true,
              render: (_value: any, row: GlobalClient) => getCreatedByName(row.createdBy),
            },
            {
              key: 'createdAt',
              label: 'Criado em',
              sortable: true,
              render: (value: any) => formatDate(value),
            },
            {
              key: 'actions',
              label: 'Ações',
              sortable: false,
              minWidth: 100,
              render: (_value: any, row: GlobalClient) => (
                <ActionMenu
                  actions={[
                    {
                      key: 'edit',
                      label: 'Editar',
                      icon: Icons.Pencil,
                      onClick: () => openEdit(row),
                    },
                    {
                      key: 'delete',
                      label: 'Excluir',
                      icon: Icons.Trash,
                      onClick: () => deleteRow(row),
                      variant: 'danger',
                    },
                  ]}
                />
              ),
            },
          ]}
          data={paginatedRows}
          keyExtractor={(row: GlobalClient, index: number) => row._id || `${row.clientGlobalId}-${index}`}
          loading={loading}
          sortColumn={sortColumn}
          sortDirection={sortDir}
          onSort={(column: string, direction?: 'asc' | 'desc') => {
            if (direction) {
              setSortColumn(column);
              setSortDir(direction);
            } else if (sortColumn === column) {
              setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
            } else {
              setSortColumn(column);
              setSortDir('asc');
            }
            setCurrentPage(1);
          }}
          filters={columnFilters}
          onFilter={(columnKey: string, value: string) => {
            setColumnFilters((prev) => ({ ...prev, [columnKey]: value }));
            setCurrentPage(1);
          }}
          striped
          hover
          responsive
          emptyMessage="Nenhum cliente global encontrado"
          pagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={filteredRows.length}
          onPageChange={(page: number) => setCurrentPage(page)}
          onPageSizeChange={(size: number) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
        />

        <div className="global-clients-footer">
          <p className="text-muted mb-0">
            {filteredRows.length} {filteredRows.length === 1 ? 'cliente global' : 'clientes globais'}
          </p>
        </div>
      </div>

      {showModal && editing && (
        <div className="global-clients-modal-overlay" onClick={closeModal}>
          <div className="global-clients-modal" onClick={(e) => e.stopPropagation()}>
            <div className="global-clients-modal-header">
              <h3>{creating ? 'Novo Cliente Global' : 'Editar Cliente Global'}</h3>
              <button className="global-clients-modal-close" onClick={closeModal} aria-label="Fechar">
                ×
              </button>
            </div>
            <div className="global-clients-modal-body">
              <div className="mb-3">
                <label className="form-label">
                  Nome do Cliente <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editing.clientName}
                  onChange={(e) => setEditing({ ...editing, clientName: e.target.value })}
                  placeholder="Nome da empresa/cliente"
                  autoFocus
                />
              </div>
              {creating && (
                <small className="text-muted">O Client Global ID será gerado automaticamente.</small>
              )}
            </div>
            <div className="global-clients-modal-footer">
              <button className="btn btn-secondary global-clients-modal-action" onClick={closeModal} disabled={saving}>
                Cancelar
              </button>
              <button className="btn btn-primary global-clients-modal-action" onClick={saveRow} disabled={saving}>
                {saving ? (creating ? 'Criando...' : 'Salvando...') : creating ? 'Criar' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.visible && <div className={`global-clients-toast global-clients-toast-${toast.variant}`}>{toast.message}</div>}

      {confirmState.visible && (
        <div className="global-clients-modal-overlay" onClick={hideConfirm}>
          <div className="global-clients-modal global-clients-modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="global-clients-modal-header">
              <h3>{confirmState.title}</h3>
              <button className="global-clients-modal-close" onClick={hideConfirm} aria-label="Fechar">
                ×
              </button>
            </div>
            <div className="global-clients-modal-body">
              <p>{confirmState.message}</p>
            </div>
            <div className="global-clients-modal-footer">
              <button className="btn btn-secondary" onClick={hideConfirm}>
                Cancelar
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  confirmState.onConfirm?.();
                  hideConfirm();
                }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalClients;
